import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Shield, User as UserIcon, Wrench, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { listUsers, createUser, updateUser, deleteUser } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PermissionsMatrix from "@/components/PermissionsMatrix";

const emptyForm = { email: "", name: "", role: "vendedora", vendedor_name: "", commission_pct: 0.10, password: "" };

export default function UsersManager({ open, onOpenChange }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [subOpen, setSubOpen] = useState(false);
  const [permOpen, setPermOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await listUsers()); }
    catch (err) {
      console.error("Erro ao carregar usuários:", err);
      toast.error("Erro ao carregar usuários");
    }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setSubOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...emptyForm, ...u, password: "" }); setSubOpen(true); };

  const handleSave = async () => {
    if (!form.email || !form.name || (!editing && !form.password)) {
      toast.error("Preencha e-mail, nome e senha"); return;
    }
    // Vendedora obrigatoriamente precisa de vendedor_name; admin/técnico podem opcionalmente
    // preencher para também aparecer em rankings/comissões/atividades.
    if (form.role === "vendedora" && !form.vendedor_name.trim()) {
      toast.error("Informe o nome da vendedora (bate com a coluna 'Vendedor' dos clientes)"); return;
    }
    try {
      if (editing) {
        const payload = {
          name: form.name,
          role: form.role,
          vendedor_name: form.vendedor_name.trim(),
          commission_pct: form.vendedor_name.trim() ? (Number(form.commission_pct) || 0) : 0,
        };
        if (form.password) payload.password = form.password;
        await updateUser(editing.id, payload);
        toast.success("Usuário atualizado");
      } else {
        await createUser({
          email: form.email.toLowerCase().trim(),
          name: form.name,
          role: form.role,
          vendedor_name: form.vendedor_name.trim(),
          commission_pct: form.vendedor_name.trim() ? (Number(form.commission_pct) || 0) : 0,
          password: form.password,
        });
        toast.success("Usuário criado");
      }
      setSubOpen(false); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este usuário?")) return;
    try { await deleteUser(id); toast.success("Usuário excluído"); load(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao excluir");
    }
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-users">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" /> Usuários
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-end mb-2 gap-2">
          <Button variant="outline" onClick={() => setPermOpen(true)} data-testid="btn-open-permissoes">
            <ShieldCheck className="w-4 h-4 mr-2" /> Permissões
          </Button>
          <Button onClick={openCreate} data-testid="btn-add-user"><Plus className="w-4 h-4 mr-2" /> Novo usuário</Button>
        </div>

        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Papel</TableHead>
                <TableHead>Vendedor</TableHead>
                <TableHead className="text-right">Comissão</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Carregando…</TableCell></TableRow>}
              {!loading && users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nenhum usuário.</TableCell></TableRow>}
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      u.role === "admin" ? "bg-primary/15 text-primary border-primary/40" :
                      u.role === "tecnico" ? "bg-sky-500/15 text-sky-500 border-sky-500/40" :
                      "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
                    }>
                      {u.role === "admin" ? <Shield className="w-3 h-3 mr-1" /> :
                       u.role === "tecnico" ? <Wrench className="w-3 h-3 mr-1" /> :
                       <UserIcon className="w-3 h-3 mr-1" />}
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.vendedor_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-mono text-sm">
                    {u.vendedor_name
                      ? `${((Number(u.commission_pct) || 0) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`btn-edit-user-${u.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(u.id)} disabled={u.id === me?.id} data-testid={`btn-delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Sub-dialog form */}
        <Dialog open={subOpen} onOpenChange={setSubOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              <div className="sm:col-span-2 space-y-1.5">
                <Label>E-mail *</Label>
                <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setF("email", e.target.value)} data-testid="user-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Nome *</Label>
                <Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="user-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Papel</Label>
                <Select value={form.role} onValueChange={(v) => setF("role", v)}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="tecnico">Técnico</SelectItem>
                    <SelectItem value="vendedora">Vendedora</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "vendedora" && (
                <div className="sm:col-span-2 space-y-1.5">
                  <Label>Nome do vendedor (bate com a coluna Vendedor) *</Label>
                  <Input value={form.vendedor_name} onChange={(e) => setF("vendedor_name", e.target.value)} placeholder="Ex: Ana Souza" data-testid="user-vendedor-name" />
                </div>
              )}
              {form.role !== "vendedora" && (
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="flex items-center gap-1.5">
                    <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                    Também atua como vendedor(a) <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
                  </Label>
                  <Input
                    value={form.vendedor_name}
                    onChange={(e) => setF("vendedor_name", e.target.value)}
                    placeholder="Ex: Lucas Voloch — deixe em branco para não aparecer em comissões/atividades"
                    data-testid="user-vendedor-name-optional"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Preenchendo esse nome, o {form.role === "admin" ? "admin" : "técnico"} passa a aparecer no ranking, nas comissões e no relatório de atividades — mantendo o acesso total.
                  </p>
                </div>
              )}
              {form.vendedor_name.trim() && (
                <div className="sm:col-span-2 space-y-1.5">
                  <Label>Comissão personalizada (%)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={(Number(form.commission_pct) || 0) * 100}
                      onChange={(e) => setF("commission_pct", (Number(e.target.value) || 0) / 100)}
                      data-testid="user-commission-pct"
                      className="max-w-[140px]"
                    />
                    <span className="text-xs text-muted-foreground">
                      aplicada sobre o valor de venda de cada plano fechado
                    </span>
                  </div>
                </div>
              )}
              <div className="sm:col-span-2 space-y-1.5">
                <Label>{editing ? "Nova senha (deixe em branco para não alterar)" : "Senha *"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setF("password", e.target.value)} data-testid="user-password" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSubOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} data-testid="btn-save-user">{editing ? "Salvar" : "Criar"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Matriz de permissões */}
        <PermissionsMatrix open={permOpen} onOpenChange={setPermOpen} />
      </DialogContent>
    </Dialog>
  );
}
